import React, { FC, MouseEvent, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	IconButton,
	Modal,
	Popover,
	SxProps,
	Tab,
	Tabs,
	Theme,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { endOfDay, endOfMonth, format, parseISO, startOfDay, startOfMonth } from 'date-fns';
import Link from 'next/link';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { useInView } from 'react-intersection-observer';
import CircularProgress from '@mui/material/CircularProgress';
import Masonry from '@mui/lab/Masonry';
import { groupBy, isEmpty, last, map, uniq, uniqBy } from 'lodash-es';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { defaultHostColorHex, hostPageStyles } from './host-page.styles';
import HeaderV3 from '@/modules/_core/components/header/header-v3.component';
import {
	communityProfilesService,
	eventsService,
	membershipsService,
	eventBundlesService,
} from '@/modules/_core/services';
import { AUTH_PAGE_URL, isClient, queryKeys } from '@/modules/_core/constants';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import LinkifyText from '@/modules/_core/components/LinkifyText';
import MarkdownText from '@/modules/_core/components/MarkdownText';
import useLink from '@/modules/_core/hooks/useLink';
import { DarkFooter } from '@/modules/_core/components/footer/dark-footer.component';
import EventCard from '../../components/event-card/event-card.component';
import { EventDatePicker } from '../../components/event-date-picker';
import MakeAppQrCode from '@/modules/_core/components/cards/Make-app-qr-code';
import MapBlock from '@/modules/_core/components/map-block/map-block.component';
import { getDarken } from '@/modules/_core/utils/color';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import {
	EventInstanceSortEnum,
	FileVariantsEnum,
	findUserToMembershipByTierId,
	OrderDto,
	membershipQueryKeys,
	eventBundleQueryKeys,
} from '@sweatpals/shared';
import { authStore } from '../../../../stores';
import { ENABLE_MEMBERSHIP_UI } from '@/modules/_core/constants/features';
import { MembershipCard } from '../../components/membership-card/membership-card.component';
import { EventBundleCard } from '@/modules/event/components/event-bundle-card/event-bundle-card.component';
import { MembershipFormModal } from '../../components/membership-form-modal/membership-form-modal.component';
import { OrderModal } from '../../../ticket/components/order';
import {
	Button as SharedButton,
	CoreButton,
	useStyles,
	Showable,
	CalendarEmptyIcon,
	MembershipIcon,
	UnstarIcon,
	clsx,
	Colors,
	SortIcon,
	ProBadge,
} from '@sweatpals/react-ui-kit';
import CreateOwnEventSection from '../../../event/components/create-own-event-block/create-own-event-block.component';
import { PerksSection } from '../../components/perks-section/perks-section.component';
import DownloadBanner from '@/public/assets/images/download-banner.webp';
import HostPageLoader from './host-page.loader';
import { useSearchParams } from 'next/navigation';
import OGMeta, { getHostSEOMeta, OGMetaProps } from '@/modules/_core/components/OGMeta';
import { useGetPublicUserByUserNameQuery, usePrefetchGetPublicEventInstanceByAliasQuery } from '@/modules/_core/hooks/queries';
import { CommunityEventCalendar } from '../../../embed/pages/embed-event-calendar/embed-event-calendar.page';
import { EventBundleFormModal } from '@/modules/event/components/event-bundle-form-modal/event-bundle-form-modal.component';
import { useCommunityLevelFeatureFlag } from '@sweatpals/react-shared';
import { JoinButton } from '../../components/join-button.component';

export enum HostTabsEnum {
	EVENTS = 'events',
	CALENDAR = 'calendar',
	MEMBERSHIP = 'membership',
	PERKS = 'perks',
}

export interface HostTabProps {
	id: string;
	value: HostTabsEnum;
	label: string | ReactNode;
	sx?: SxProps<Theme>
}

const CustomTabPanel: FC<PropsWithChildren<{ tab: HostTabsEnum; value: HostTabsEnum }>> = ({
	tab,
	value,
	children,
}) => (
	<Box role="tabpanel" hidden={value !== tab} id={`host-tabs_${tab}`}>
		{tab === value && children}
	</Box>
);

const LinkifyHrefDecorator = (decoratedHref: string, decoratedText: string, key: number) => (
	<a
		key={key}
		href={decoratedHref}
		style={{
			color: Colors['FA5634'],
		}}
	>
		{decoratedText}
	</a>
);

export interface HostPageProps {
	userName: string;
	defaultHostTab?: HostTabsEnum;
	isBot?: boolean;
}

// eslint-disable-next-line complexity
const HostPage: FC<HostPageProps> = observer(({ userName, defaultHostTab, isBot }) => {
	const theme = useTheme();
	const router = useRouter();
	const params = useSearchParams();
	const user = authStore.user;

	const { openDeepLink, makeHostLink, makeDeepProfileLink, makeSearchEventsLink } = useLink();
	const { ref: upcommingEventsHeaderRef, inView: upcommingEventsHeaderInView } = useInView();

	const [selectedMonthId, setSelectedMonthId] = useState(format(new Date(), 'yyyy-MM'));
	const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
	const [isOpenDatePikerPopup, setIsOpenDatePikerPopup] = useState(false);
	const [isOpenDatePikerModal, setIsOpenDatePikerModal] = useState(false);

	const [membershipFormModalIsOpen, setMembershipFormModalIsOpen] = useState(false);
	const [selectedMembershipTierId, setSelectedMembershipTierId] = useState<string | null>(null);
	const [membershipOrderModalIsOpen, setMembershipOrderModalIsOpen] = useState(false);
	const [membershipOrder, setMembershipOrder] = useState<OrderDto | null>(null);
	const [isMembershipSelectDisabled, setIsMembershipSelectDisabled] = useState(false);
	const [isMembershipPreselected, setIsMembershipPreselected] = useState(false);

	const [isEventBundlePreselected, setIsEventBundlePreselected] = useState(false);
	const [selectedEventBundleId, setSelectedEventBundleId] = useState<string | null>(null);
	const [isEventBundleSelectDisabled, setIsEventBundleSelectDisabled] = useState(false);
	const [eventBundleFormModalIsOpen, setEventBundleFormModalIsOpen] = useState(false);
	const [eventBundleOrderModalIsOpen, setEventBundleOrderModalIsOpen] = useState(false);
	const [eventBundleOrder, setEventBundleOrder] = useState<OrderDto | null>(null);

	const [tabValue, setTabValue] = useState<HostTabsEnum>(defaultHostTab ?? HostTabsEnum.EVENTS);

	const checkout = useMemo(() => params.get(membershipQueryKeys.getMembership) || params.get(eventBundleQueryKeys.getEventBundle), [params.get(membershipQueryKeys.getMembership), params.get(eventBundleQueryKeys.getEventBundle)]);
	const preselectedMembershipTierId = useMemo(() => params.get(membershipQueryKeys.membershipTierId), [params.get(membershipQueryKeys.membershipTierId)]);
	const preselectedEventBundleId = useMemo(() => params.get(eventBundleQueryKeys.eventBundleId), [params.get(eventBundleQueryKeys.eventBundleId)]);
	const currentDayId = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
	const tomorrowDayId = useMemo(() => {
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		return format(tomorrow, 'yyyy-MM-dd');
	}, []);

	const { data: hostData, isLoading: isHostDataLoading } = useGetPublicUserByUserNameQuery(userName);
	const prefetchEventDetails = usePrefetchGetPublicEventInstanceByAliasQuery();

	const appHostLink = useMemo(() => makeDeepProfileLink(hostData?.id), [hostData?.id, makeDeepProfileLink]);
	const hostPageUrl = useMemo(() => makeHostLink({ userName }), [userName, makeHostLink]);

	const openAppHostLink = useCallback(() => {
		if (hostData?.id) {
			openDeepLink({ userName: hostData?.userName, isProfilePage: true }, true);
		}
	}, [openDeepLink, hostData?.userName]);

	const primaryColor = useMemo(() => hostData?.primaryColor ?? defaultHostColorHex, [hostData?.primaryColor]);
	const backgroundColor = useMemo(() => getDarken(primaryColor, 1), [primaryColor]);

	const { data: eventsDates = [], isFetching: eventsDatesIsLoading } = useQuery(
		queryKeys.eventsDates(hostData?.id, selectedMonthId),
		async () => {
			const periodFrom = parseISO(selectedMonthId);
			const periodTo = parseISO(selectedMonthId);

			periodFrom.setMonth(periodFrom.getMonth() - 1);
			periodTo.setMonth(periodTo.getMonth() + 1);

			return eventsService.getPublicEventsDates({
				creatorsIds: [hostData?.id],
				periodFrom: startOfMonth(periodFrom),
				periodTo: endOfMonth(periodTo),
			});
		}
	);

	const {
		data: upcomingEventsData,
		fetchNextPage,
		hasNextPage,
		isFetching: upcomingEventsIsFetching,
		isLoading: upcomingEventsIsLoading,
	} = useInfiniteQuery(
		queryKeys.relatedEvents(hostData?.id, 'upcoming-events'),
		async ({ pageParam }) => {
			const eventsList = await eventsService.getPublicExtendEventsList({
				creatorsIds: [hostData?.id],
				lastEventId: pageParam?.lastEventId,
				periodFrom: pageParam?.periodFrom,
				limit: 10,
				sort: EventInstanceSortEnum.InstanceAsc,
				withUnverifiedEvents: true,
			});

			const baseIds = uniq(eventsList.data.map(e => e.baseId));

			return eventsList;
		},
		{
			getNextPageParam: lastPage => {
				if (lastPage?.hasMore) {
					return {
						periodFrom: last(lastPage.data).startDate,
						lastEventId: lastPage.lastEventId,
					};
				}

				return null;
			},
		}
	);

	const isBioInMarkdown = useCommunityLevelFeatureFlag('isBioInMarkdown', hostData);

	const upcomingEvents = useMemo(
		() =>
			upcomingEventsData?.pages?.reduce((acc, page) => ({
				...acc,
				...page,
				data: [...acc.data, ...page.data],
			})),
		[upcomingEventsData?.pages]
	);

	const fetchMoreData = useCallback(() => {
		fetchNextPage();
	}, [fetchNextPage]);

	const {
		data: dateEvents,
		isFetching: dateEventsIsLoading,
	} = useQuery(queryKeys.relatedEvents(hostData?.id, selectedDateId), async () => {
		if (!selectedDateId) {
			return null;
		}

		const eventsList = await eventsService.getPublicExtendEventsList({
			creatorsIds: [hostData?.id],
			periodFrom: startOfDay(parseISO(selectedDateId)),
			periodTo: endOfDay(parseISO(selectedDateId)),
		});

		return eventsList;
	});

	const groupedEvents = useMemo(() => {
		if (selectedDateId) {
			return dateEvents?.data?.length ? { [selectedDateId]: dateEvents?.data ?? [] } : {};
		}

		return groupBy(upcomingEvents?.data ?? [], event =>
			format(new Date(event.instance ?? event.startDate), 'yyyy-MM-dd'));
	}, [selectedDateId, dateEvents?.data, upcomingEvents?.data]);

	const markers = useMemo(() => {
		const events = selectedDateId ? dateEvents?.data : upcomingEvents?.data;

		return uniqBy(events ?? [], event => `${event.addressLat}-${event.addressLng}`)
			.map(event => ({
				position: {
					lat: Number(event.addressLat),
					lng: Number(event.addressLng),
				},
				addressName: event.addressName,
			}))
			.map((marker, index) => ({
				...marker,
				element: `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52" fill="none">
					<circle cx="25.5134" cy="25.5134" r="25.5134" fill="${backgroundColor}" fill-opacity="0.5"/>
					<circle cx="25.5126" cy="25.5136" r="12.7567" fill="${primaryColor}"/>
					<circle cx="25.5128" cy="25.5128" r="9.56754" fill="#252525"/>
					<text x="49%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-family="Montserrat" font-size="13px" font-weight="700">
						${index + 1}
					</text>
				</svg>`,
			}));
	}, [selectedDateId, dateEvents?.data, upcomingEvents?.data, backgroundColor, primaryColor]);

	const groupedEventsDates = useMemo(
		() => groupBy(eventsDates, date => format(new Date(date), 'yyyy-MM-dd')),
		[eventsDates]
	);

	const styles = useStyles(hostPageStyles, primaryColor, backgroundColor);
	const isLessThanLg = useMediaQuery(theme.breakpoints.down('lg'));
	const isLessThanMd = useMediaQuery(theme.breakpoints.down('md'));
	const isLessThanSm = useMediaQuery(theme.breakpoints.down('sm'));

	const onOpenDatePickerPopup = useCallback((e: MouseEvent<HTMLElement>) => {
		setIsOpenDatePikerPopup(true);
		setDatePickerAnchorEl(e.currentTarget);
	}, []);
	const onCloseDatePickerPopup = useCallback(() => {
		setIsOpenDatePikerPopup(false);
		setDatePickerAnchorEl(null);
	}, []);

	const onOpenDatePickerModal = useCallback(() => {
		setIsOpenDatePikerModal(true);
	}, []);
	const onCloseDatePickerModal = useCallback(() => {
		setIsOpenDatePikerModal(false);
	}, []);

	const prefetchEventDetailsWithSource = useCallback(async (eventAlias: string, shortLocalInstance?: string) => {
		await prefetchEventDetails(eventAlias, shortLocalInstance, 'event-page.component');
	}, [prefetchEventDetails]);

	const DatePicker = useMemo(
		() => (
			<EventDatePicker
				isLoading={eventsDatesIsLoading}
				groupedEventsDates={groupedEventsDates}
				primaryColor={primaryColor}
				value={selectedDateId ? parseISO(selectedDateId) : null}
				onChange={date => {
					setSelectedDateId(format(date, 'yyyy-MM-dd'));
					setSelectedMonthId(format(date, 'yyyy-MM'));
				}}
				onMonthChange={date => {
					setSelectedMonthId(format(date, 'yyyy-MM'));
				}}
				onYearChange={date => {
					setSelectedMonthId(format(date, 'yyyy-MM'));
				}}
				dayOfWeekFormatter={(value, date) => (date ? format(date, 'EEE').toUpperCase() : value)}
			/>
		),
		[eventsDatesIsLoading, groupedEventsDates, primaryColor, selectedDateId]
	);

	const handleChangeTab = useCallback((newValue: HostTabsEnum) => {
		setTabValue(newValue);
		router.push(`${hostPageUrl}/${newValue}`);
	}, [hostPageUrl, router]);

	const { data: hostMemberships, isFetched: isHostMembershipsFetched, isFetching: isHostMembershipsFetching } = useQuery(
		queryKeys.hostMemberships(hostData?.id),
		async () => membershipsService.getPublishedMembershipsByCommunityId(hostData?.id),
		{ enabled: !!hostData?.id }
	);

	const { data: hostEventBundles, isFetched: isHostEventBundlesFetched, isFetching: isHostEventBundlesFetching } = useQuery(
		queryKeys.hostEventBundles(hostData?.id),
		async () => eventBundlesService.getPublishedEventBundlesByCommunityId(hostData?.id),
		{ enabled: !!hostData?.id }
	);

	const publicHostEventBundles = useMemo(() => (hostEventBundles ?? []), [hostEventBundles]);

	const publicHostMemberships = useMemo(() => (hostMemberships ?? []).filter(m => !m.isUnlisted), [hostMemberships]);

	useEffect(() => {
		if (!publicHostMemberships.length && tabValue === HostTabsEnum.MEMBERSHIP && isHostMembershipsFetched && !hostEventBundles?.length && !isHostEventBundlesFetched) {
			handleChangeTab(HostTabsEnum.EVENTS);
		}
	}, [publicHostMemberships, tabValue, isHostMembershipsFetched, hostEventBundles, isHostEventBundlesFetched]);

	const { data: userMemberships = [], refetch: refetchUserMemberships, isFetching: isUserMembershipsFetching } = useQuery(
		queryKeys.userMemberships(user?.id, 'active'),
		async () => membershipsService.getCurrentUserMemberships(true),
		{ enabled: !!user?.id }
	);

	const { data: userEventBundles = [], refetch: refetchUserEventBundles, isFetching: isUserEventBundlesFetching } = useQuery(
		queryKeys.userEventBundles(user?.id),
		async () => eventBundlesService.getUserEventBundles({
			withEventBundle: true,
			active: true,
		}),
		{ enabled: !!user?.id }
	);

	const boughtMembership = useMemo(
		() => findUserToMembershipByTierId(userMemberships, membershipOrder?.membershipTierId),
		[userMemberships, membershipOrder?.membershipTierId]
	);

	const boughtEventBundle = useMemo(
		() => userEventBundles?.find((eventBundle) => eventBundle.id === eventBundleOrder?.eventBundleId),
		[userEventBundles, eventBundleOrder?.eventBundleId]
	);

	const openMembershipFormModal = useCallback((tierId?: string) => {
		if (!hostMemberships?.length) {
			return;
		}

		setMembershipFormModalIsOpen(true);

		if (tierId) {
			setSelectedMembershipTierId(tierId);
		}
	}, [hostMemberships?.length]);
	const closeMembershipFormModal = useCallback(() => {
		setMembershipFormModalIsOpen(false);
		setIsMembershipSelectDisabled(false);
	}, []);

	const openEventBundleFormModal = useCallback((eventBundleId?: string) => {
		if (!hostEventBundles?.length) {
			return;
		}

		setEventBundleFormModalIsOpen(true);

		if (eventBundleId) {
			setSelectedEventBundleId(eventBundleId);
		}
	}, [hostEventBundles?.length]);

	const closeEventBundleFormModal = useCallback(() => {
		setEventBundleFormModalIsOpen(false);
		setIsEventBundleSelectDisabled(false);
	}, []);

	// auto open membership modal
	useEffect(() => {
		if (!hostMemberships?.length || isMembershipPreselected || !checkout) {
			return;
		}

		const membershipsTiers = hostMemberships.map(m => m.tiers).flat();
		const preselectedMembershipTier = membershipsTiers.find(t => t.id === preselectedMembershipTierId);

		if (preselectedMembershipTier) {
			openMembershipFormModal(preselectedMembershipTier?.id);

			if (preselectedMembershipTier) {
				setIsMembershipSelectDisabled(true);
			}

			setIsMembershipPreselected(true);
		}
	}, [
		checkout,
		hostMemberships,
		publicHostMemberships.length,
		preselectedMembershipTierId,
		isMembershipPreselected,
		openMembershipFormModal
	]);

	// auto open event bundle modal
	useEffect(() => {
		if (!hostEventBundles?.length || isEventBundlePreselected || !checkout) {
			return;
		}

		const preselectedEventBundle = hostEventBundles.find(t => t.id === preselectedEventBundleId);

		if (preselectedEventBundle && publicHostEventBundles.length) {
			openEventBundleFormModal(preselectedEventBundle?.id);

			if (preselectedEventBundle) {
				setIsEventBundleSelectDisabled(true);
			}

			setIsEventBundlePreselected(true);
		}
	}, [
		checkout,
		hostEventBundles,
		publicHostEventBundles.length,
		preselectedEventBundleId,
		isEventBundlePreselected,
		openEventBundleFormModal
	]);

	const { data: perksCount, isFetching: isPerksCountFetching } = useQuery(
		queryKeys.hostRelated(hostData?.id, 'perks-count'),
		async () =>
			communityProfilesService.getCommunityPerksCount(hostData?.id, {
				withMembershipPerks: true,
			}),
		{
			enabled: !!hostData?.id && !!hostData?.isCommunityProfile,
		}
	);

	const openEventOrderModalIsOpen = useCallback(async (order?: OrderDto) => {
		if (!order) {
			return;
		}

		if (order.membershipTierId) {
			await refetchUserMemberships();
			setMembershipOrder(order);
			setMembershipOrderModalIsOpen(true);
		}

		if (order.eventBundleId) {
			await refetchUserEventBundles();
			setEventBundleOrder(order);
			setEventBundleOrderModalIsOpen(true);
		}
	}, [refetchUserMemberships, refetchUserEventBundles]);
	const closeEventOrderModalIsOpen = useCallback(() => {
		setMembershipOrderModalIsOpen(false);
		setMembershipOrder(null);
		setSelectedMembershipTierId(null);
		setEventBundleOrderModalIsOpen(false);
		setEventBundleOrder(null);
		setSelectedEventBundleId(null);
	}, []);

	const hostBackgroundUrl = useMemo(
		() =>
			(hostData?.backgroundId
			? getPublicFileUrl(hostData?.backgroundId, {
				variant: FileVariantsEnum.S,
			}) : undefined),
		[hostData?.backgroundId]
	);

	const hostTabs = useMemo((): HostTabProps[] => {
		if ((!publicHostMemberships.length && !publicHostEventBundles.length) || !ENABLE_MEMBERSHIP_UI) {
			return [];
		}

		const tabs = [
			{
				id: 'host-tabs_events',
				value: HostTabsEnum.EVENTS,
				label: (
					<>
						<CalendarEmptyIcon width={20} height={20}/>
						<Typography variant='inherit'>
							Events
						</Typography>
					</>
				)
			}
		];

		if ((publicHostMemberships.length || publicHostEventBundles.length) && ENABLE_MEMBERSHIP_UI) {
			tabs.push({
				id: "host-tabs_membership",
				value: HostTabsEnum.MEMBERSHIP,
				label: (
					<>
						<MembershipIcon width={18} height={18} />
						<Typography variant='inherit'>
							Memberships
						</Typography>
					</>
				)
			});
		}

		if (perksCount && perksCount.count > 0) {
			tabs.push({
				id: 'host-tabs_perks',
				value: HostTabsEnum.PERKS,
				label: (
					<>
						<UnstarIcon width={24} height={24} />
						<Typography variant='inherit'>
							Perks
						</Typography>
					</>
				)
			});
		}

		return tabs;
	}, [publicHostMemberships.length, perksCount]);

	const searchEventsLink = useMemo(
		() => makeSearchEventsLink({ cityId: hostData?.homeAddressCityId }),
		[hostData?.homeAddressCityId, makeSearchEventsLink]
	);

	const onLogin = useCallback(() => {
		window.open(
			`${AUTH_PAGE_URL}?redirect=${window.location.href}`,
			'_self',
		);
	}, [])

	const isAllDataLoaded = useMemo(() =>
		!isHostDataLoading &&
		!upcomingEventsIsLoading &&
		!isHostMembershipsFetching &&
		!isHostEventBundlesFetching &&
		!isUserMembershipsFetching &&
		!isPerksCountFetching,
		[
			isHostDataLoading,
			upcomingEventsIsLoading,
			isHostMembershipsFetching,
			isHostEventBundlesFetching,
			isUserMembershipsFetching,
			isPerksCountFetching
		]
	);

	const seo: OGMetaProps = useMemo(() => (isBot ? {} : getHostSEOMeta(hostData)), [hostData, isBot]);

	return (
	<>
		<OGMeta {...seo} />
		<Box sx={styles.root}>
			<Showable show={isLessThanSm && !upcommingEventsHeaderInView}>
				<Box sx={styles.header}>
					<Box display="flex" gap="24px" alignItems="center">
						<Showable hide={selectedDateId}>
							<Typography sx={styles.headerTitle}>Upcoming Events</Typography>
						</Showable>
						<Showable show={selectedDateId}>
							<Button
								sx={styles.headerButton}
								onClick={() => {
									setSelectedDateId(null);
								}}
							>
								<CloseIcon />
								Clear Filters
							</Button>
						</Showable>
					</Box>
					<Showable show={isLessThanLg}>
						<Button
							sx={{ ...styles.headerButton, width: '124px' }}
							onClick={isLessThanSm ? onOpenDatePickerModal : onOpenDatePickerPopup}
						>
							Calendar
						</Button>
					</Showable>
				</Box>
			</Showable>
			<Box sx={styles.content}>
				<HeaderV3 sx={{ position: 'fixed', backgroundColor: 'rgba(17, 20, 18, 0.6)' }} />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						p: { xs: '0 0 30px', md: '70px 30px', lg: '70px' },
						position: 'relative',
						borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
					}}
				>
					<Showable show={isAllDataLoaded}>
					<Box sx={{ ...styles.background }} />
					<Box sx={{ maxWidth: '1250px', width: '100%', zIndex: 2 }}>
						<Box sx={styles.hostInfoSection}>
							<Box sx={styles.hostInfo}>
								{/* <Showable show={hostData?.homeAddressCity}>*/}
								{/*	<Box sx={styles.navigation}>*/}
								{/*		<Link href={searchEventsLink} passHref legacyBehavior>*/}
								{/*			<Typography sx={clsx(styles.navigationItem, styles.navigationLink)}>*/}
								{/*				{hostData?.homeAddressCity?.name}*/}
								{/*			</Typography>*/}
								{/*		</Link>*/}
								{/*		<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10">*/}
								{/*			<path d="M0 9.67982V7.11482L5.865 5.17982V4.84982L0 2.91482V0.319824L8.25 3.34982V6.67982L0 9.67982Z" fill="currentColor"/>*/}
								{/*		</svg>*/}
								{/*		<Typography sx={styles.navigationItem}>{hostData?.fullName}</Typography>*/}
								{/*	</Box>*/}
								{/* </Showable>*/}
								<Box sx={styles.hostInfoContent}>
									<RemoteImage
										alt="host avatar"
										src={hostData?.avatarId}
										sx={styles.authorAvatar}
										width={styles.authorAvatar.width}
										height={styles.authorAvatar.height}
									/>
									<Box>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
												marginBottom: '9px',
											}}
										>
											<Typography sx={styles.hostName}>{hostData?.fullName}</Typography>
											<Showable show={hostData?.isProPlanActive}>
												<ProBadge withCrown />
											</Showable>
										</Box>
										<Typography
											sx={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500, lineHeight: '17px', fontFamily: 'Montserrat', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '17px' }}
										>
											<b style={{fontWeight: '700'}}>{hostData?.followersCount}</b>
											<span>members</span>
											<span>·</span>
											<b style={{fontWeight: '700'}}>{hostData?.eventsCount}</b>
											<span>events</span>
											<span>·</span>
											<b style={{fontWeight: '700'}}>{hostData?.addresses?.[0]?.addressName ? hostData?.addresses?.[0]?.addressName : ''}</b>
											<Showable show={hostData?.addresses?.length > 1}>
												<Box sx={{ padding: '2px 5px', display: 'flex', alignItems: 'center', backgroundColor: '#2A2A2A', borderRadius: '540px' }}>
													{hostData?.addresses?.length > 1 && `+${hostData?.addresses?.length - 1}`}
												</Box>
											</Showable>
										</Typography>
										<Showable show={hostData?.profileChatCid && !isLessThanSm}>
											<JoinButton username={userName} sx={styles.joinChatButton}/>
										</Showable>
										{/* <Showable show={hostData?.isBusinessProfile}>*/}
										{/*	<Box sx={styles.hostVerifiedBox}>*/}
										{/*		<ImageWrapper>*/}
										{/*			<Image src={verifyIcon} alt={'verify icon'} width={30} height={30} />*/}
										{/*		</ImageWrapper>*/}
										{/*		<Box id={'verify'} sx={styles.hostVerifiedTextBox}>*/}
										{/*			<Typography sx={styles.hostVerifiedText}>SweatPals verified</Typography>*/}
										{/*		</Box>*/}
										{/*	</Box>*/}
										{/* </Showable>*/}
										<Box sx={styles.hostBioText}>
											{isBioInMarkdown ? (
												<MarkdownText color="inherit" linkColor="inherit">
													{hostData?.myBio}
												</MarkdownText>
											) : (
												<LinkifyText componentDecorator={LinkifyHrefDecorator} >{hostData?.myBio}</LinkifyText>
											)}
										</Box>
									</Box>
								</Box>
							</Box>
						</Box>
						<Showable show={hostTabs.length}>
							<Tabs
								value={tabValue}
								onChange={(e, value: HostTabsEnum) => {
									handleChangeTab(value);
								}}
								TabIndicatorProps={{
									sx: {
										backgroundColor: primaryColor,
									},
								}}
								sx={{
									mt: 2,
									'& .MuiTabs-scroller': {
										borderBottom: '2px solid #2f3430',
										overflow: 'visible !important',
									},
									'& .MuiTabs-flexContainer': {
										gap: '12px',
										overflowX: 'scroll',
										'&::-webkit-scrollbar': {
											display: 'none',
										},
										scrollbarWidth: 'none',
									},
								}}
							>
								{hostTabs.map(hostTab => (
									<Tab
										{...hostTab}
										key={hostTab.id}
										sx={clsx(styles.hostTab, hostTab.sx)}
									/>
								))}
							</Tabs>
						</Showable>

						<CustomTabPanel tab={HostTabsEnum.EVENTS} value={tabValue}>
							<Box sx={{
								paddingTop: '24px',
							}}>
								<Box sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '2px',
								}}>
									<Button
										onClick={() => handleChangeTab(HostTabsEnum.EVENTS)}
										sx={styles.eventListTypeButton(HostTabsEnum.EVENTS, tabValue)}
									>
										<SortIcon width={18} height={18} color="rgba(0, 0, 0, 0.5)" />
										List
									</Button>
									<Button
										onClick={() => handleChangeTab(HostTabsEnum.CALENDAR)}
										sx={styles.eventListTypeButton(HostTabsEnum.CALENDAR, tabValue)}
									>
										<CalendarEmptyIcon width={18} height={18} />
										Calendar
									</Button>
								</Box>

								<Box sx={styles.upcomingEventsSection}>
									<Showable hide={upcomingEvents?.totalCount}>
										<Box sx={{ ...styles.placeholder, padding: { xs: '31px', md: '62px' } }}>
											<Typography sx={styles.placeholderIcon}>👀</Typography>
											<Typography sx={styles.placeholderTitle}>
												Oops, this community doesn&apos;t have events yet
											</Typography>
											<Typography sx={styles.placeholderDescription}>
												Stay tuned for updates, join their community chats to see what they are up to.
											</Typography>
													<Showable show={hostData?.profileChatCid}>
														<JoinButton username={userName} sx={styles.joinChatButton}/>
											</Showable>
										</Box>
									</Showable>
									<Showable show={upcomingEvents?.totalCount}>
										<Box sx={styles.upcomingEventsHeader} ref={upcommingEventsHeaderRef}>
											<Box display="flex" gap="24px" alignItems="center">
												<Showable hide={isLessThanSm && selectedDateId}>
													<Typography sx={styles.upcomingEventsTitle}>
														{selectedDateId ? format(parseISO(selectedDateId), 'dd MMM, eeee') : 'Upcoming Events'}
													</Typography>
												</Showable>
												<Showable show={selectedDateId}>
													<Button
														sx={styles.button}
														onClick={() => {
															setSelectedDateId(null);
														}}
													>
														<CloseIcon />
														Clear Filters
													</Button>
												</Showable>
											</Box>
											<Showable show={isLessThanLg}>
												<Button
													sx={{ ...styles.button, width: '124px', background: '#343434' }}
													onClick={isLessThanSm ? onOpenDatePickerModal : onOpenDatePickerPopup}
												>
													<CalendarEmptyIcon width={20} height={20}/>
													Calendar
												</Button>
											</Showable>
										</Box>
										<Box display="flex" gap="44px">
											<Box flex={1}>
												<Showable show={selectedDateId ? dateEventsIsLoading : false}>
													<Box
														sx={{
															width: '100%',
															height: '320px',
															display: 'flex',
															justifyContent: 'center',
															alignItems: 'center',
														}}
													>
														<CircularProgress sx={{ color: 'white' }} />
													</Box>
												</Showable>
												<Showable hide={selectedDateId ? dateEventsIsLoading : false}>
													<Showable hide={isEmpty(groupedEvents)}>
														<Box display="flex" flexDirection="column" gap="40px">
															<Box>
																{map(groupedEvents, (events, dayId) => {
																	const titleIsVisible = isLessThanSm || !selectedDateId;

																	return (
																		<Box
																			key={dayId}
																			sx={{ ...styles.dayEvents, paddingTop: titleIsVisible ? '30px' : 0 }}
																		>
																			<Box
																				sx={{
																					...styles.dayEventsMarker,
																					backgroundColor:
																						dayId === selectedDateId || dayId === currentDayId ? '#FFFFFF' : '#202020',
																				}}
																			/>
																			<Showable show={titleIsVisible}>
																				<Typography sx={styles.dayEventsTitle}>
																					<Box component="span">
																						{currentDayId === dayId
																							? 'Today'
																							: tomorrowDayId === dayId
																							? 'Tomorrow'
																							: format(parseISO(dayId), 'dd MMM,')}
																					</Box>
																					{` ${format(parseISO(dayId), 'eeee')}`}
																				</Typography>
																			</Showable>
																			<Box display="flex" flexDirection="column" gap="24px">
																				{events.map(event => (
																					<EventCard
																						key={event.id}
																						event={event}
																						user={authStore.user}
																						primaryColor={primaryColor}
																						onHostLinkClick={prefetchEventDetailsWithSource}
																					/>
																				))}
																			</Box>
																		</Box>
																	);
																})}
															</Box>
															<Box>
																<Showable show={hasNextPage && !selectedDateId}>
																	<Box display="flex" justifyContent="center">
																		<SharedButton
																			sx={styles.hasNextButton}
																			onClick={fetchMoreData}
																			disabled={upcomingEventsIsFetching}
																			loading={upcomingEventsIsFetching}
																			loadingSpinnerSize={20}
																		>
																			Load More
																		</SharedButton>
																	</Box>
																</Showable>
															</Box>
															<Showable show={isClient && markers.length && isLessThanLg}>
																<Box sx={styles.mapBlock}>
																	<MapBlock markers={markers} variant="dark" height="260px" zoom={18} />
																	{/* <Box sx={styles.mapBlockLink}>*/}
																	{/*	<Link href={searchEventsLink}>*/}
																	{/*		Explore more in {hostData?.homeAddressCity?.name}*/}
																	{/*	</Link>*/}
																	{/* </Box>*/}
																</Box>
															</Showable>
															<Box sx={{ ...styles.banner, backgroundImage: `url(${DownloadBanner.src})` }}>
																<Box sx={styles.bannerContent}>
																	<Box sx={styles.innerBannerContent}>
																		<Typography sx={styles.bannerTitle}>
																			Join {hostData?.fullName ?? hostData?.userName} on the SweatPals app
																		</Typography>
																		<Typography sx={styles.bannerDescription}>
																			Exclusive event signups & community groupchats to connect with your workout and
																			accountability buddies.
																		</Typography>
																	</Box>
																	<Showable hide={isLessThanMd}>
																		<Box sx={{ borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.10)', padding: '10px' }}>
																			<MakeAppQrCode
																				link={appHostLink}
																				hideCopyLinkButton
																				qrCodeWrapperSx={{
																					padding: '0',
																					backgroundColor: '#131415',
																				}}
																				qrCodeSx={{
																					padding: '0',
																					borderRadius: '14px',
																					border: '1px solid #000000',
																				}}
																				size={130}
																			/>
																		</Box>
																	</Showable>
																</Box>
																<Showable show={isLessThanMd}>
																	<Box display="flex" justifyContent="center">
																		<Button
																			onClick={openAppHostLink}
																			sx={styles.secondaryButton}
																		>
																			Join
																		</Button>
																	</Box>
																</Showable>
															</Box>
															<Box sx={{'& > div': {border: 'none'}, '& button': {fontFamily: 'Montserrat', fontWeight: '600', fontSize: '14px'}}}>
															<CreateOwnEventSection primaryColor={primaryColor}></CreateOwnEventSection>
															</Box>
														</Box>
													</Showable>
													<Showable show={isEmpty(groupedEvents)}>
														<Box sx={{ ...styles.placeholder, gap: '20px' }}>
															<Typography sx={styles.placeholderIcon}>👀</Typography>
															<Typography sx={{ ...styles.placeholderTitle, mb: '6px' }}>
																Oops, no events for this date
															</Typography>
															<Typography sx={styles.placeholderDescription}>
																Stay tuned for updates, join their community chats to see what they are up to.
															</Typography>
															<Button
																onClick={() => {
																	setSelectedDateId(null);
																}}
																sx={{
																	...styles.button,
																	width: '196px',
																	height: '41px',
																}}
															>
																View upcoming events
															</Button>
														</Box>
													</Showable>
												</Showable>
											</Box>
											<Showable hide={isLessThanLg}>
												<Box width="318px" display="flex" flexDirection="column" gap="24px">
													{DatePicker}
													<Showable show={typeof window !== undefined && markers.length}>
														<Box sx={styles.mapBlock}>
															<MapBlock markers={markers} variant="dark" height="260px" zoom={18} />
															<Box sx={styles.mapBlockLink}>
																<Link href={searchEventsLink}>
																	Explore more in {hostData?.homeAddressCity?.name}
																</Link>
															</Box>
														</Box>
													</Showable>

													<Link href='/create/community' legacyBehavior>
														<CoreButton sx={styles.launchOwnCommunityButton}>
															Create my community for FREE
														</CoreButton>
													</Link>
												</Box>
											</Showable>
										</Box>
									</Showable>
								</Box>
							</Box>
						</CustomTabPanel>

						<CustomTabPanel tab={HostTabsEnum.CALENDAR} value={tabValue}>
							<Box sx={{
								paddingTop: '24px',
							}}>
								<Box sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '2px',
									px: { xs: '10px', md: '0px' },
								}}>
									<Button
										onClick={() => handleChangeTab(HostTabsEnum.EVENTS)}
										sx={styles.eventListTypeButton(HostTabsEnum.EVENTS, tabValue)}
									>
										<SortIcon width={18} height={18} color="rgba(0, 0, 0, 0.5)" />
										List
									</Button>
									<Button
										onClick={() => handleChangeTab(HostTabsEnum.CALENDAR)}
										sx={styles.eventListTypeButton(HostTabsEnum.CALENDAR, tabValue)}
									>
										<CalendarEmptyIcon width={18} height={18} />
										Calendar
									</Button>
								</Box>

								<Box sx={{
									mt: '32px',
									overflowX: 'auto',
									px: { xs: '10px', md: '0px' },
								}}>
									<CommunityEventCalendar
										entityAlias={hostData?.userName}
										showInfoPopup={false}
										showLargePopup={false}
										primaryColorHex='ffffff'
										headerFontColorHex='737373'
										popupBackgroundColorHex='000000'
									/>
								</Box>
							</Box>
						</CustomTabPanel>

						<Showable show={ENABLE_MEMBERSHIP_UI}>
							<CustomTabPanel tab={HostTabsEnum.MEMBERSHIP} value={tabValue}>
								<Box sx={styles.membershipContent}>
									<Box sx={styles.membershipBox}>
										<Masonry columns={2} spacing={3}>
											{publicHostMemberships.slice()
												.sort(membs => (membs.isPinned ? -1 : 0))
												.map((memship, _idx, arr) => (
													<MembershipCard
														key={memship.id}
														hostPrimaryColorHex={primaryColor}
														membership={memship}
														sx={styles.membershipCardSx(publicHostMemberships.length
															 + publicHostEventBundles.length)}
														drawBackground={memship.isPinned}
														icon={
															memship.isPinned ? (
																<RemoteImage alt='host avatar' src={hostData?.avatarId} width={45} height={45} sx={{
																	borderRadius: '50%',
																}} />
															) : undefined
														}
														onGetMembership={openMembershipFormModal}
													/>
												))
											}
											{
												hostEventBundles?.map(eventBundle => (
												<EventBundleCard
													key={eventBundle.id}
													eventBundle={eventBundle}
													hostPrimaryColorHex={primaryColor}
													sx={styles.membershipCardSx(publicHostMemberships.length
														 + publicHostEventBundles.length)}
													onGetEventBundle={openEventBundleFormModal}
												/>
												))
											}
										</Masonry>
									</Box>
								</Box>
							</CustomTabPanel>
						</Showable>

						<CustomTabPanel tab={HostTabsEnum.PERKS} value={tabValue}>
							<Box sx={styles.perksContent}>
								<PerksSection
									hostData={hostData}
									onGetMembership={() => {
										openMembershipFormModal();
									}}
									onLogin={onLogin}
									communityMemberships={hostMemberships}
								/>
							</Box>
						</CustomTabPanel>

					</Box>
					</Showable>

					<Showable hide={isAllDataLoaded}>
						<HostPageLoader />
					</Showable>
				</Box>

				<Showable hide={tabValue === HostTabsEnum.MEMBERSHIP}>
					{/* <Showable show={hostData?.profileChatCid && isLessThanSm}>
						<Box sx={styles.footer}>
							<SharedButton
								onClick={() => {
									handleJoinCommunity();
								}}
								sx={{ ...styles.joinChatButton, height: '38px', width: '224px' }}
								loading={isJoinCommunityLoading}
								disabled={isJoinCommunityLoading}
							>
								Join community
							</SharedButton>
						</Box>
					</Showable> */}
					<DarkFooter />
					<Showable show={isLessThanSm}>
						<Modal
							open={isOpenDatePikerModal}
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#120E0DE5',
							}}
						>
							<>
								<IconButton
									onClick={onCloseDatePickerModal}
									sx={{
										color: '#FFFFFF',
										border: '1px solid #FFFFFF66',
										position: 'absolute',
										top: '13px',
										right: '24px',
									}}
								>
									<CloseIcon />
								</IconButton>
								{DatePicker}
							</>
						</Modal>
					</Showable>
					<Showable hide={isLessThanSm}>
						<Popover
							id="date-picker-popover"
							open={isOpenDatePikerPopup}
							anchorEl={datePickerAnchorEl}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'left',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
							sx={{
								'& .MuiPaper-root': {
									backgroundColor: '#120E0D',
									borderRadius: '8px',
								},
							}}
							onClose={onCloseDatePickerPopup}
							disableRestoreFocus
						>
							{DatePicker}
						</Popover>
					</Showable>
				</Showable>
			</Box>

			<Showable show={ENABLE_MEMBERSHIP_UI}>
				{
					hostMemberships?.length
					&& membershipFormModalIsOpen
					&& (publicHostMemberships.length || selectedMembershipTierId)
					&& (
						<MembershipFormModal
							isOpen={membershipFormModalIsOpen}
							onClose={closeMembershipFormModal}
							hostData={hostData}
							isMembershipSelectDisabled={isMembershipSelectDisabled}
							preselectedMembershipTierId={selectedMembershipTierId}
							onSubmit={openEventOrderModalIsOpen}
							hostMemberships={hostMemberships}
						/>
					)
				}
				<OrderModal
					open={membershipOrderModalIsOpen}
					onClose={closeEventOrderModalIsOpen}
					orderId={membershipOrder?.id}
					host={hostData}
					hostEvents={upcomingEvents?.data}
					userMembership={boughtMembership}
				/>
			</Showable>

			<Showable show={ENABLE_MEMBERSHIP_UI}>
				{
					hostEventBundles?.length
					&& eventBundleFormModalIsOpen
					&& (publicHostEventBundles.length || selectedEventBundleId)
					&& (
						<EventBundleFormModal
							isOpen={eventBundleFormModalIsOpen}
							onClose={closeEventBundleFormModal}
							hostData={hostData}
							isEventBundleSelectDisabled={isEventBundleSelectDisabled}
							preselectedEventBundleId={selectedEventBundleId}
							onSubmit={openEventOrderModalIsOpen}
							hostEventBundles={hostEventBundles}
						/>
					)
				}
				<OrderModal
					open={eventBundleOrderModalIsOpen}
					onClose={closeEventOrderModalIsOpen}
					orderId={eventBundleOrder?.id}
					host={hostData}
					hostEvents={upcomingEvents?.data}
					userEventBundle={boughtEventBundle}
				/>
			</Showable>
		</Box>
	</>
	);
});

export default HostPage;
